import { ChangeEvent, FocusEvent, useState } from "react";

// The character counter appears once the field is this close to its limit.
const LENGTH_FOR_WARNING = 100;

type FieldElement = HTMLInputElement | HTMLTextAreaElement;

type Options<E extends FieldElement> = {
  value?: string | number | readonly string[];
  required?: boolean;
  // Marks the form as submitted, which makes an empty required field warn even
  // if the user never focused it.
  formSubmitted?: boolean;
  maxLength?: number;
  onChange?: (evt: ChangeEvent<E>) => void;
  onFocus?: (evt: FocusEvent<E>) => void;
  onBlur?: (evt: FocusEvent<E>) => void;
};

type FieldFeedback<E extends FieldElement> = {
  // Appended to the control's class and the tooltip's: "", "required",
  // "length-warning" or "length-error".
  stateClass: string;
  tooltip: string;
  isInvalid: () => boolean;
  clearState: () => void;
  // Spread onto the control after its own props, so these wrap the caller's.
  handlers: {
    onChange: (evt: ChangeEvent<E>) => void;
    onFocus: (evt: FocusEvent<E>) => void;
    onBlur: (evt: FocusEvent<E>) => void;
  };
};

/**
 * The warning a legacy form field shows for itself: "* Required field" once an
 * empty required field has been visited or its form submitted, and a countdown
 * as the value approaches `maxLength`.
 *
 * Shared by formElements' Input and Textarea, which render their own control so
 * each stays typed to the element it owns.
 */
export default function useFieldFeedback<E extends FieldElement>(
  options: Options<E>
): FieldFeedback<E> {
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);
  const [charactersLeft, setCharactersLeft] = useState(LENGTH_FOR_WARNING + 1);

  // An unset value counts as empty; a numeric zero does not.
  const isEmpty = options.value === undefined || options.value === "";
  const isInvalid = () => isEmpty && (touched || options.formSubmitted === true);

  let stateClass = "";
  let tooltip = "";
  if (options.required && isInvalid()) {
    stateClass = "required";
    tooltip = "* Required field";
  } else if (charactersLeft <= LENGTH_FOR_WARNING && focused) {
    stateClass = charactersLeft > 0 ? "length-warning" : "length-error";
    tooltip =
      charactersLeft > 0 ? `${charactersLeft} characters left` : "Field length reached";
  }

  return {
    stateClass,
    tooltip,
    isInvalid,
    clearState: () => {
      setTouched(false);
      setFocused(false);
      setCharactersLeft(LENGTH_FOR_WARNING + 1);
    },
    handlers: {
      onChange: (evt: ChangeEvent<E>) => {
        if (options.maxLength) {
          setCharactersLeft(options.maxLength - evt.target.value.length);
        }
        options.onChange?.(evt);
      },
      onFocus: (evt: FocusEvent<E>) => {
        setFocused(true);
        options.onFocus?.(evt);
      },
      onBlur: (evt: FocusEvent<E>) => {
        setTouched(true);
        setFocused(false);
        options.onBlur?.(evt);
      },
    },
  };
}
